"use client";

import { useRouter, useSearchParams } from "next/navigation";

import ErrorMessage from "@/app/component/error-message";
import PrimaryButtonGradient from "@/app/component/gradientbutton";
import { useFormik } from "formik";
import { MouseEventHandler, useEffect, useState } from "react";
import * as Yup from "yup";

import CountDownTimer from "@/app/component/countdowntimer";
import OtpInput, { AllowedInputTypes } from "react-otp-input";

import { InputTextBox } from "@/app/component/InputTextBox";
import MobileSelect from "@/app/component/mobileselect/mobileselectinput";
import { useClientConfigProvider } from "@/app/config/client-config-provider";
import { EventLabels } from "@/app/constants/mix-panel-enums";
import AppRoutes from "@/app/constants/routes";
import { useMixPanel } from "@/app/context/mixPanelContext";
import ToastUtils from "@/app/toast/toast-utils";
import { getPlatformAndBrowser } from "@/app/utility/common";
import { countryCodesArr } from "@/app/utils/countryCodesArray";
import {
  LoginFlow,
  UniversalStorageConstants,
  UniversalStorageUtils,
  useAppContext,
} from "@thriwe-2-0/thriwe-ui-utils";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { LoginInfo } from "../login-with-otp";
import { SignInUI } from "./common-signin-ui";
import { VerifiedComponent } from "./verified-ui";

export function OTPPage({
  preLoginObject,
  editOnClick,
  switchSignInType,
  verificationType = "sign in",
  setshowOtpVerify = () => {},
  changeToken = () => {},
}: {
  switchSignInType: string;
  preLoginObject: LoginInfo | undefined;
  editOnClick?: MouseEventHandler<HTMLImageElement>;
  verificationType?: string;
  setshowOtpVerify?: Function;
  changeToken?: Function;
}) {
  const context = useAppContext();
  const { t } = useTranslation();

  const { trackEvent } = useMixPanel();
  const {
    instances: { apiRepository },
    state: { user },
    fetchUserProfile,
  } = context;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [showOtpMessage, setshowOtpMessage] = useState<boolean>(false);
  const { config, getImagePath } = useClientConfigProvider();

  const router = useRouter();
  const [otp, setOtp] = useState("");
  const [showTimer, setShowTimer] = useState(true);

  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("");

  const [token, setToken] = useState("");
  const [fromWhere, setFromWhere] = useState("");
  const searchParams = useSearchParams();
  const [hasError, setHasError] = useState<boolean>(false);
  useEffect(() => {
    if (verificationType == "sign in") {
      trackEvent(EventLabels.SL_OTP, { screen: "Login" });
      trackEvent(EventLabels.SL_OTP_Done, { screen: "Login" });
    }
  }, []);

  useEffect(() => {
    if (preLoginObject != null) {
      setCountryCode(preLoginObject.countryCode ?? "");
      setMobile(preLoginObject.mobile ?? "");
      setEmail(preLoginObject?.email ?? "");
      setToken(preLoginObject?.token ?? "");
      setFromWhere(preLoginObject?.fromWhere);
    } else {
      router.push(AppRoutes.login);
    }
  }, []);

  useEffect(() => {
    if (
      user?.data?.firstName &&
      config?.login?.loginFlow == LoginFlow.LOGIN_REGISTER_WITH_MOBILE_OTP
    ) {
      setTimeout(() => {
        router.push(AppRoutes.dashboard);
      }, 3000);
    }
  }, [user?.data]);
  const handleInputChange = (event: any) => {
    if (event.key === "Enter") {
      confirmOTP();
    }
  };

  const confirmOTP = () => {
    if (otp.length < (config?.login?.otpConfig?.otpLength ?? 4)) {
      ToastUtils.showErrorToast(t("registerLogin.enterOtpPrompt"));
    } else {
      setIsLoading(true);
      trackEvent(EventLabels.CTA_Submit_OTP, { screen: "Login" });
      const deviceName =
        config?.limitMaxSessions == true ? getPlatformAndBrowser() : undefined;

      if (verificationType == "sign in") {
        trackEvent(EventLabels.CTA_Submit_OTP, { screen: "Login" });
        if (process.env.PROJECT_CODE == "MASTERCARD_CL" && window.gtag) {
          window.gtag("event", "conversion", {
            allow_custom_scripts: true,
            send_to: "DC-12016036/tksk/tapka000+standard",
          });
        }
      }

      apiRepository
        ?.verifyOTP(otp, token, undefined, deviceName)
        .then((v) => {
          setIsLoading(false);
          let response = v?.data.user;
          console.log("user = ", response);
          if (verificationType == "sign in") {
            UniversalStorageUtils.setItem(
              config?.nativeDataSavingType,
              UniversalStorageConstants.AUTH_TOKEN,
              v.headers.authorization
            );

            setshowOtpMessage(true);
            if (
              config?.login?.loginFlow == LoginFlow.LOGIN_WITH_MOBILE_EMAIL_OTP
            ) {
              setTimeout(() => {
                router.push(AppRoutes.dashboard);
              }, 3000);
            }
            setTimeout(() => {
              setIsVerified(true);
            }, 1500);
          } else {
            ToastUtils?.showSuccessToast(
              t("registerLogin.verifiedSuccessfully")
            );
          }
          setshowOtpVerify(false);
          changeToken("");
          setshowOtpMessage(true);
          if (verificationType == "sign in") {
            fetchUserProfile();
          }
          if (!config?.profile?.profile?.verificationFromSaveButton) {
            fetchUserProfile();
          }
          if (response) {
            return v?.data?.user;
          }
        })
        .catch((e) => {
          setHasError(true);
          setTimeout(() => {
            setHasError(false);
          }, 2000);
          setIsLoading(false);
          trackEvent(EventLabels.CTA_Submit_OTP, {
            screen: "Login",
            error: e?.response?.data?.message ?? t("common.errorMessage"),
          });

          ToastUtils.showErrorToast(
            e?.response?.data?.message ?? t("common.errorMessage")
          );
        });
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        t("registerLogin.enterEmailPrompt")
      )
      .required(
        t("common._isRequiredField", {
          field: t("common.email", { format: "capitalize" }),
        })
      ),
    lastName: Yup.string()
      .required(t("common._isRequiredField", { field: t("common.lastName") }))
      .matches(/^[A-Za-z]+( [A-Za-z]+)*$/, t("registerLogin.enterNamePrompt"))
      .max(30, "Max 30 characters"),
    firstName: Yup.string()
      .required(t("common._isRequiredField", { field: t("common.firstName") }))
      .matches(/^[A-Za-z]+( [A-Za-z]+)*$/, t("registerLogin.enterNamePrompt"))
      .max(30, "Max 30 characters"),
  });

  const handleDivClick = (handleFormSubmit: any) => {
    console.log("handleDivClick");
    handleFormSubmit();
  };

  const handleFormSubmit = (values: any) => {
    setIsLoading(true);
    apiRepository
      ?.updateUser({
        user: {
          firstName: values?.firstName,
          lastName: values?.lastName,
          email: !user?.data?.email ? values?.email : null,
        },
      })
      .then((v: any) => {
        fetchUserProfile();
        setTimeout(() => {
          router?.push(AppRoutes.dashboard);
        }, 3000);
        setIsLoading(false);
        console.log("updated data", v?.data);
      })
      .catch((e: any) => {
        ToastUtils.showErrorToast(e?.response?.data?.message);
        setIsLoading(false);
        console.log("error", e);
      });
  };

  const signUpForm = useFormik({
    initialValues: {
      firstName: null,
      email: "",
      lastName: "",
      mobile: mobile,
    },
    validationSchema: validationSchema,
    onSubmit: handleFormSubmit,
    enableReinitialize: true,
    isInitialValid: false,
    validateOnBlur: false,
    validateOnMount: true,
  });

  const registerComponent = () => {
    const {
      values,
      handleBlur,
      handleChange,
      touched,
      errors,
      submitForm,
      setFieldTouched,
    } = signUpForm;
    const item = {
      name: "Mobile Number",
      variableName: "mobile",
      editable: false,
      type: "number",
      labelName: "",
    };
    return (
      <div>
        <div className="login-flex-container">
          <SignInUI switchSignInType={"email"} islogin={false} />

          <div className="input-label-group login-flex-container-item">
            <div className="label" style={{ marginBottom: "0px" }}>
              {t("common.phoneNumber", { format: "capitalize" })}
            </div>
            <MobileSelect
              isDisabled={true}
              handleChange={handleChange}
              handleBlur={handleBlur}
              formik={signUpForm}
              countryCode={countryCode}
              countryCodesArr={countryCodesArr}
              setCountryCode={setCountryCode}
              errors={errors}
              values={values}
              touched={touched}
              item={item}
              hasError={touched?.mobile != null && errors?.mobile != null}
            />
            <div
              style={{
                display: touched?.mobile ? errors?.mobile ?? "block" : "none",
                marginTop: touched?.mobile
                  ? errors?.mobile ?? "3px"
                  : undefined,
              }}
            >
              <ErrorMessage
                message={
                  touched?.mobile && errors?.mobile
                    ? errors?.mobile ?? ""
                    : undefined
                }
                touched={touched?.mobile}
              />
            </div>
          </div>

          <div>
            <div className="input-label-group login-flex-container-item">
              <div className="label" style={{ marginBottom: "0px" }}>
                {t("common.firstName", { format: "capitalize" })}
              </div>
              <InputTextBox
                hint={"first name"}
                onChange={handleChange}
                onBlur={handleBlur}
                name="firstName"
                value={values.firstName}
                placeholder={t("common.firstName")}
                hasError={
                  touched?.firstName != null && errors?.firstName != null
                }
              />
              <div
                style={{
                  display:
                    touched?.firstName != null && errors?.firstName != null
                      ? "block"
                      : "none",
                  marginTop: touched?.firstName
                    ? errors?.firstName ?? "3px"
                    : undefined,
                }}
              >
                <ErrorMessage
                  message={
                    touched?.firstName ? errors?.firstName ?? "" : undefined
                  }
                  touched={touched?.firstName}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="input-label-group login-flex-container-item">
              <div className="label" style={{ marginBottom: "0px" }}>
                Last Name
              </div>
              <InputTextBox
                hint={"Last Name"}
                onChange={handleChange}
                onBlur={handleBlur}
                name="lastName"
                value={values.lastName}
                placeholder={t("common.firstName")}
                hasError={touched?.lastName != null && errors?.lastName != null}
              />
              <div
                style={{
                  display:
                    touched?.lastName != null && errors?.lastName != null
                      ? "block"
                      : "none",
                  marginTop: touched?.lastName
                    ? errors?.lastName ?? "3px"
                    : undefined,
                }}
              >
                <ErrorMessage
                  message={
                    touched?.lastName ? errors?.lastName ?? "" : undefined
                  }
                  touched={touched?.lastName}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="input-label-group login-flex-container-item">
              <div className="label" style={{ marginBottom: "0px" }}>
                {t("enterYour.emailAddress")}
              </div>
              <InputTextBox
                hint={"Email Address"}
                onChange={handleChange}
                onBlur={handleBlur}
                name="email"
                value={values.email}
                placeholder={t("common.emailAddress")}
                hasError={touched?.email != null && errors?.email != null}
              />
              <div
                style={{
                  display:
                    touched?.email != null && errors?.email != null
                      ? "block"
                      : "none",
                  marginTop: touched?.email
                    ? errors?.email ?? "3px"
                    : undefined,
                }}
              >
                <ErrorMessage
                  message={touched?.email ? errors?.email ?? "" : undefined}
                  touched={touched?.email}
                />
              </div>
            </div>
          </div>
        </div>

        <PrimaryButtonGradient
          isDisabled={!signUpForm?.isValid}
          margin="20px 0px 10px 0px"
          name={t("linkMembership.startRedeeming")}
          fullWidth={true}
          onClick={() => handleDivClick(submitForm)}
          isLoading={isLoading}
          classNameDiv="buttonDiv"
          showArrow={true}
          arrowIcon={<span></span>}
          classNameBackgroundStylePrimaryGradient="buttonBackgroundStylePrimaryGradient2"
          classNamePrimaryIsLoading="buttonBackgroundStylePrimaryGradient2"
        />
      </div>
    );
  };

  const returnVerifiedComponent = () => {
    if (
      config?.login?.loginFlow == LoginFlow.LOGIN_REGISTER_WITH_MOBILE_OTP &&
      isVerified &&
      user?.data?.firstName
    )
      return <VerifiedComponent />;
    else if (
      config?.login?.loginFlow == LoginFlow.LOGIN_WITH_MOBILE_EMAIL_OTP &&
      isVerified
    )
      return <VerifiedComponent />;
  };

  return (
    <>
      <div style={{ position: "relative" }}>
        {config?.login?.loginFlow == LoginFlow.LOGIN_REGISTER_WITH_MOBILE_OTP &&
        isVerified
          ? !user?.data?.firstName && registerComponent()
          : null}

        {returnVerifiedComponent()}
        {!isVerified ? (
          <div className="login-flex-container">
            <SignInUI verificationType={verificationType} />

            <div className="login-flex-container-item">
              {
                <div
                  className="login-sub-label"
                  style={{
                    margin: "0px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {t("registerLogin.verificationSent", {
                    method:
                      switchSignInType !== "mobile"
                        ? t("common.email")
                        : t("common.mobile"),
                  })}
                  <span className="otp-mobile">
                    {`${
                      switchSignInType !== "mobile"
                        ? email
                        : countryCode + " " + mobile
                    }`}
                  </span>
                </div>
              }
              <div className="mt-1 mb-1">
                <div className="login-sub-label mb-3" style={{ margin: "0px" }}>
                  {t("registerLogin.enterOTP")}
                </div>

                <OtpInput
                  inputType={
                    config?.login?.otpConfig?.inputType as AllowedInputTypes
                  }
                  value={otp}
                  onChange={setOtp}
                  numInputs={config?.login?.otpConfig?.otpLength ?? 4}
                  containerStyle="otp-input-container"
                  inputStyle={`otp-input ${hasError ? "error" : ""}`}
                  renderInput={(props) => (
                    <input onKeyUp={handleInputChange} {...props} />
                  )}
                />
              </div>
              <div className="login-register-here-text">
                {showTimer ? (
                  <>
                    <span className="login-register-link">
                      {t("registerLogin.resendOtpIn")}&nbsp;
                    </span>

                    <div>
                      <span className="login-register-link" onClick={() => {}}>
                        <CountDownTimer
                          hoursMinSec={{
                            hours: 0,
                            minutes: 0,
                            seconds:
                              config?.login?.otpConfig?.resendOtpTimer ?? 30,
                          }}
                          format={{
                            hours: false,
                            minutes: true,
                            seconds: true,
                          }}
                          cbFunc={() => {
                            setShowTimer(false);
                          }}
                        />
                      </span>
                    </div>
                  </>
                ) : (
                  <span
                    role="button"
                    className="login-register-link resend-otp resend-otp-options-container"
                    onClick={() => {
                      trackEvent(EventLabels.CTA_ResendOTP, {
                        screen: "Login",
                      });
                      apiRepository
                        ?.resendOTP({ token })
                        .then((v) => {
                          setOtp("");
                          ToastUtils.showSuccessToast(v?.data?.message);
                          var token = v.headers.authorization;
                          setShowTimer(true);
                          setToken(token);
                          if (verificationType != "sign in") {
                            changeToken(token);
                          }
                        })
                        .catch((error) => {
                          ToastUtils.showErrorToast(
                            error?.response?.data?.message
                          );
                          console.log("error", error);
                        });
                    }}
                  >
                    {!config?.login?.otpConfig?.resendOtp?.isShowBothOptions &&
                      config?.login?.otpConfig?.resendOtp?.onSms &&
                      config?.login?.otpConfig?.resendOtp?.onWhatsapp && (
                        <span className="resend-otp-both">
                          {t("registerLogin.resendOTP")}
                        </span>
                      )}

                    <>
                      {config?.login?.otpConfig?.resendOtp?.isShowBothOptions &&
                        config?.login?.otpConfig?.resendOtp?.onSms && (
                          <div className="otp-resend-container">
                            <div className="sms-resend-otp-icon">
                              <Image
                                src={getImagePath("resend-otp-sms.png")}
                                alt=""
                                width="18"
                                height="18"
                              />
                            </div>
                            <p className="sms-resend-otp-text">
                              {t("registerLogin.resendOTPviaSMS")}
                            </p>
                          </div>
                        )}

                      {config?.login?.otpConfig?.resendOtp?.isShowBothOptions &&
                        config?.login?.otpConfig?.resendOtp?.onWhatsapp && (
                          <div
                            className="otp-resend-container"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              trackEvent(EventLabels.CTA_ResendOTP, {
                                screen: "Login",
                              });
                              apiRepository
                                ?.resendOTP({
                                  token,
                                  bodyMoreParams: { resendOtpOnWhatsApp: true },
                                })
                                .then((v) => {
                                  setOtp("");
                                  ToastUtils.showSuccessToast(v?.data?.message);
                                  var token = v.headers.authorization;
                                  setShowTimer(true);
                                  setToken(token);
                                  if (verificationType != "sign in") {
                                    changeToken(token);
                                  }
                                })
                                .catch((error) => {
                                  ToastUtils.showErrorToast(
                                    error?.response?.data?.message
                                  );
                                });
                            }}
                          >
                            <div className="whatsapp-resend-otp-icon">
                              <Image
                                src={getImagePath("resend-otp-whatsapp.png")}
                                alt=""
                                width="18"
                                height="18"
                              />
                            </div>
                            <p className="whatsapp-resend-otp-text">
                              {t("registerLogin.resendOTPviaWhatsApp")}
                            </p>
                          </div>
                        )}
                    </>
                  </span>
                )}
              </div>
            </div>
            {showOtpMessage ? (
              <div className="success-message-alert">
                {t("registerLogin.OtpVerified")}
              </div>
            ) : (
              <PrimaryButtonGradient
                secondary={false}
                classNameBackgroundStylePrimaryGradient="buttonBackgroundStylePrimaryGradient2"
                classNamePrimaryIsLoading="buttonBackgroundStylePrimaryGradient2"
                isDisabled={otp.length < 4 ? true : false}
                isLoading={isLoading}
                name={t("common.verify")}
                classNameDiv="buttonDiv"
                onClick={confirmOTP}
                fullWidth={true}
              />
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
